import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { fetchDevices, type Device, ApiError } from "@/lib/api";
import { CopyBox } from "@/components/CopyBox";
import { DeviceCard } from "@/components/DeviceCard";

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "loaded"; devices: Device[]; licenseKey: string };

export function Setup() {
  const { licenseKey: authLicenseKey } = useAuth();
  const [state, setState] = useState<LoadState>({ status: "loading" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const data = await fetchDevices();
      setState({
        status: "loaded",
        devices: data.devices,
        licenseKey: data.licenseKey,
      });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `Server error (${err.status})`
          : "Failed to load devices. Check your connection and try again.";
      setState({ status: "error", message });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.status === "loading") {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="space-y-6">
          <div className="skeleton h-8 w-48 rounded-lg" />
          <div className="skeleton h-4 w-96 rounded" />
          <div className="skeleton h-32 rounded-xl" />
          <div className="skeleton h-32 rounded-xl" />
        </div>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-6 h-6 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-zinc-100 mb-2">
            Something went wrong
          </h2>
          <p className="text-sm text-zinc-400 mb-6">{state.message}</p>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-medium px-5 py-2.5 rounded-lg text-sm transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
              />
            </svg>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const { devices, licenseKey } = state;
  const key = licenseKey || authLicenseKey || "YOUR_LICENSE_KEY";
  const hasDevices = devices.length > 0;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 animate-fade-in">
      <div className="mb-10">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">
          Setup your Blazeboard
        </h1>
        <p className="text-zinc-400">
          {hasDevices
            ? "Your devices are listed below. Use the install commands to add more."
            : "Get your digital signage system running in minutes with a single command."}
        </p>
      </div>

      <div className="space-y-8">
        <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-4 h-4 text-blue-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7m0 0a3 3 0 01-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z"
                />
              </svg>
            </div>
            <div>
              <h2 className="font-semibold text-zinc-100">
                Server Kit
              </h2>
              <p className="text-sm text-zinc-500">
                Runs the Blazeboard server, admin UI, and display engine
              </p>
            </div>
          </div>
          <p className="text-sm text-zinc-400 mb-4">
            Run this command on your Raspberry Pi to install the Blazeboard
            server. It sets up Docker, the orchestrator, kiosk mode, and
            automatic updates. Your license key is pre-filled.
          </p>
          <CopyBox
            label="Run this command on your Pi:"
            value={`curl -sSL https://releases.blazeboard.co/install | sudo bash -s -- --key ${key}`}
          />
        </section>

        <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-4 h-4 text-purple-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z"
                />
              </svg>
            </div>
            <div>
              <h2 className="font-semibold text-zinc-100">
                Player Kit
              </h2>
              <p className="text-sm text-zinc-500">
                Displays content from your Blazeboard server on a connected
                screen
              </p>
            </div>
          </div>
          <p className="text-sm text-zinc-400 mb-4">
            Run this on any additional Pi connected to a display. It will pair
            with your server automatically over the local network. No license
            key needed for player devices.
          </p>
          <CopyBox
            label="Run this on your player Pi:"
            value="curl -sSL https://releases.blazeboard.co/install-player | sudo bash"
          />
        </section>

        {hasDevices && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-zinc-100">
                Your devices
              </h2>
              <span className="text-sm text-zinc-500">
                {devices.length} device{devices.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="space-y-3">
              {devices.map((device) => (
                <DeviceCard key={device.id} device={device} />
              ))}
            </div>
          </section>
        )}

        {!hasDevices && (
          <section className="bg-zinc-900/50 border border-dashed border-zinc-800 rounded-xl p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-zinc-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 011.06 0z"
                />
              </svg>
            </div>
            <h3 className="font-medium text-zinc-300 mb-1">
              No devices registered yet
            </h3>
            <p className="text-sm text-zinc-500 max-w-md mx-auto">
              Run the server kit install command above on your Raspberry Pi.
              Your device will appear here once it connects.
            </p>
          </section>
        )}

        <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h2 className="font-semibold text-zinc-100 mb-3">
            Your license key
          </h2>
          <p className="text-sm text-zinc-400 mb-4">
            This key is tied to your account and is used to authenticate your
            devices during setup. Keep it private.
          </p>
          <div className="bg-zinc-800 rounded-lg px-4 py-3 font-mono text-sm text-zinc-300 flex items-center justify-between gap-3">
            <span className="truncate">{key}</span>
          </div>
        </section>
      </div>
    </div>
  );
}
